import { Node, mergeAttributes, type Editor, type NodeViewRendererProps } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

/**
 * 규정 문서 구조 노드 (CR-004). 서버 policy-doc.ts 와 같은 규칙.
 *  doc > (chapter | article | addendum | appendix | form)+
 *  article > (paragraph | table | clause)*   clause > (paragraph | table | item)*   item > (paragraph | subitem)*   subitem > paragraph*
 * 번호는 CSS 카운터로만 렌더한다 (저장하지 않음).
 * 장·조·부칙·별표·별지의 제목(별표·별지는 번호도)은 노드 속성이고, 편집기에서는 머리의 입력 칸으로 고친다 (CR-068).
 */
type HeadPart = '#' | { lit: string; paren?: boolean } | { attr: 'title' | 'no'; placeholder: string };

let measureCtx: CanvasRenderingContext2D | null = null;
/** 입력 칸 폭을 내용에 맞춘다 (화면에 붙기 전에는 글자 수로 어림: 한글 1em, 그 외 0.55em) */
function fit(i: HTMLInputElement, ro: boolean) {
  const t = i.value || (ro ? '' : i.placeholder);
  if (i.isConnected) {
    measureCtx ??= document.createElement('canvas').getContext('2d');
    if (measureCtx) {
      const cs = getComputedStyle(i);
      measureCtx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      i.style.width = `${Math.ceil(measureCtx.measureText(t).width) + 2}px`;
      return;
    }
  }
  const w = [...t].reduce((n, c) => n + (c.charCodeAt(0) > 255 ? 1 : 0.55), 0);
  i.style.width = `${Math.max(1, w + 0.2)}em`;
}

/** 머리(번호 + 제목 입력 칸) + 본문(contentDOM) NodeView */
const headView = (name: string, cls: string, parts: HeadPart[]) => ({ node, editor, getPos }: NodeViewRendererProps) => {
  const dom = document.createElement('div');
  dom.className = cls; dom.dataset.node = name;
  const head = document.createElement('div');
  head.className = 'pn-head'; head.contentEditable = 'false';
  const inputs: [string, HTMLInputElement][] = [];
  const toBody = () => {
    const pos = getPos(); if (typeof pos !== 'number') return;
    const { tr } = editor.state;
    editor.view.dispatch(tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 1))).scrollIntoView());
    editor.view.focus();
  };
  for (const p of parts) {
    if (p === '#' || 'lit' in p) {
      const s = document.createElement('span');
      s.className = p === '#' ? 'pn-no' : p.paren ? 'pn-paren' : '';
      if (p !== '#') s.textContent = p.lit;
      head.append(s);
      continue;
    }
    const i = document.createElement('input');
    i.className = 'pn-title'; i.placeholder = p.placeholder; i.spellcheck = false; i.dataset.attr = p.attr;
    i.addEventListener('input', () => {
      fit(i, false);
      const pos = getPos(); if (typeof pos !== 'number') return;
      editor.view.dispatch(editor.state.tr.setNodeAttribute(pos, p.attr, i.value));
    });
    i.addEventListener('keydown', (e) => {
      if (e.isComposing) return;
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'Escape') { e.preventDefault(); toBody(); }
    });
    inputs.push([p.attr, i]);
    head.append(i);
  }
  const body = document.createElement('div');
  body.className = 'pn-body';
  dom.append(head, body);
  let cur = node;
  const sync = () => {
    const ro = !editor.isEditable;
    dom.classList.toggle('pn-ro', ro);
    dom.classList.toggle('pn-untitled', !cur.attrs.title);
    for (const [attr, i] of inputs) {
      const v = String(cur.attrs[attr] ?? '');
      if (i.value !== v && document.activeElement !== i) i.value = v;
      i.readOnly = ro; i.tabIndex = ro ? -1 : 0;
      i.classList.toggle('pn-empty', !i.value);
      fit(i, ro);
    }
  };
  sync();
  requestAnimationFrame(sync); // 화면에 붙은 뒤 실제 글꼴로 폭을 다시 잰다
  editor.on('update', sync);
  return {
    dom, contentDOM: body,
    update: (n: typeof node) => { if (n.type !== cur.type) return false; cur = n; sync(); return true; },
    stopEvent: (e: Event) => head.contains(e.target as globalThis.Node),
    ignoreMutation: (m: MutationRecord | { type: 'selection'; target: globalThis.Node }) => head.contains(m.target),
    destroy: () => { editor.off('update', sync); },
  };
};

/** 새로 만든 구조 노드의 첫 입력 칸(제목, 별표·별지는 번호)에 포커스 */
export function focusHead(editor: Editor, pos: number) {
  requestAnimationFrame(() => {
    const dom = editor.view.nodeDOM(pos);
    if (dom instanceof HTMLElement) dom.querySelector<HTMLInputElement>(':scope > .pn-head input')?.focus();
  });
}

const structural = (name: string, content: string, opts: { group?: string; attrs?: Record<string, { default: unknown }>; cls: string; defining?: boolean; head?: HeadPart[] }) =>
  Node.create({
    name, content, group: opts.group, defining: opts.defining ?? true, isolating: false,
    addAttributes() { return opts.attrs ?? {}; },
    parseHTML() { return [{ tag: `div[data-node="${name}"]` }]; },
    renderHTML({ HTMLAttributes, node }) {
      const attrs: Record<string, string> = { 'data-node': name, class: opts.cls };
      if (typeof node.attrs.title === 'string') attrs['data-title'] = node.attrs.title;
      if (node.attrs.no != null && node.attrs.no !== '') attrs['data-no'] = String(node.attrs.no);
      return ['div', mergeAttributes(HTMLAttributes, attrs), 0];
    },
    ...(opts.head ? { addNodeView: () => headView(name, opts.cls, opts.head!) } : {}),
  });

export const Chapter = structural('chapter', 'article+', { group: 'top', attrs: { title: { default: '' } }, cls: 'pn-chapter', head: ['#', { attr: 'title', placeholder: '장 제목' }] });
export const Article = structural('article', '(paragraph | table | clause)+', { group: 'top', attrs: { title: { default: '' }, controlIds: { default: [] }, deptId: { default: null }, tags: { default: [] } }, cls: 'pn-article', head: ['#', { lit: '(', paren: true }, { attr: 'title', placeholder: '조 제목' }, { lit: ')', paren: true }] });
export const Clause = structural('clause', '(paragraph | table | item)+', { cls: 'pn-clause' });
export const Item = structural('item', '(paragraph | subitem)+', { cls: 'pn-item' });
export const Subitem = structural('subitem', 'paragraph+', { cls: 'pn-subitem' });
export const Addendum = structural('addendum', '(paragraph | table)+', { group: 'top', attrs: { title: { default: '' } }, cls: 'pn-addendum', head: [{ lit: '부칙 ' }, { attr: 'title', placeholder: '제목·공포일 등' }] });
export const Appendix = structural('appendix', '(paragraph | table | image)+', { group: 'top', attrs: { title: { default: '' }, no: { default: '' } }, cls: 'pn-appendix', head: [{ lit: '[별표 ' }, { attr: 'no', placeholder: '번호' }, { lit: '] ' }, { attr: 'title', placeholder: '별표 제목' }] });
export const Form = structural('form', '(paragraph | table | image)+', { group: 'top', attrs: { title: { default: '' }, no: { default: '' } }, cls: 'pn-form', head: [{ lit: '[별지 제' }, { attr: 'no', placeholder: '번호' }, { lit: '호 서식] ' }, { attr: 'title', placeholder: '서식 제목' }] });

export const PolicyDoc = Node.create({ name: 'doc', topNode: true, content: 'top+' });

/** 편집기 CSS (번호 카운터). 화면과 출력이 같은 번호 규칙을 쓴다. */
export const POLICY_EDITOR_CSS = `
@counter-style pn-circled { system: fixed; symbols: "①" "②" "③" "④" "⑤" "⑥" "⑦" "⑧" "⑨" "⑩" "⑪" "⑫" "⑬" "⑭" "⑮" "⑯" "⑰" "⑱" "⑲" "⑳"; suffix: ""; }
@counter-style pn-hangul { system: fixed; symbols: "가" "나" "다" "라" "마" "바" "사" "아" "자" "차" "카" "타" "파" "하"; suffix: ""; }
.pn-editor .ProseMirror { outline: none; counter-reset: chapter article; }
.pn-editor .pn-chapter { counter-increment: chapter; margin: 12px 0 6px; }
.pn-editor .pn-chapter > .pn-head { text-align: center; font-weight: 700; font-size: 17px; margin: 8px 0; }
.pn-editor .pn-chapter > .pn-head .pn-no::before { content: "제" counter(chapter) "장"; margin-right: .3em; }
.pn-editor .pn-article { counter-increment: article; counter-reset: clause; margin: 8px 0; padding: 6px 10px; border-left: 3px solid #e65100; background: #fffaf5; border-radius: 4px; }
.pn-editor .pn-article > .pn-head { font-weight: 700; margin-bottom: 4px; }
.pn-editor .pn-article > .pn-head .pn-no::before { content: "제" counter(article) "조"; }
.pn-editor .pn-clause { counter-increment: clause; counter-reset: item; display: grid; grid-template-columns: 1.4em minmax(0, 1fr); column-gap: 6px; margin: 2px 0 2px 4px; } /* 번호 열 + 내용 열: 문단·호·표가 세로로 쌓인다 */
.pn-editor .pn-clause::before { content: counter(clause, pn-circled); grid-column: 1; grid-row: 1; font-weight: 600; }
.pn-editor .pn-clause > * { grid-column: 2; min-width: 0; }
.pn-editor .pn-item { counter-increment: item; counter-reset: subitem; display: grid; grid-template-columns: 1.4em minmax(0, 1fr); column-gap: 6px; margin: 2px 0 2px 4px; }
.pn-editor .pn-item::before { content: counter(item) "."; grid-column: 1; grid-row: 1; font-weight: 600; }
.pn-editor .pn-item > * { grid-column: 2; min-width: 0; }
.pn-editor .pn-subitem { counter-increment: subitem; display: grid; grid-template-columns: 1.4em minmax(0, 1fr); column-gap: 6px; margin: 2px 0 2px 4px; }
.pn-editor .pn-subitem::before { content: counter(subitem, pn-hangul) "."; grid-column: 1; grid-row: 1; font-weight: 600; }
.pn-editor .pn-subitem > * { grid-column: 2; min-width: 0; }
.pn-editor .pn-addendum, .pn-editor .pn-appendix, .pn-editor .pn-form { margin: 10px 0; padding: 6px 10px; border: 1px dashed #cbd5e1; border-radius: 4px; }
.pn-editor .pn-addendum > .pn-head, .pn-editor .pn-appendix > .pn-head, .pn-editor .pn-form > .pn-head { font-weight: 700; }
.pn-editor .pn-head { white-space: pre-wrap; user-select: none; }
.pn-editor .pn-title { font: inherit; color: inherit; background: transparent; border: 0; border-bottom: 1px dashed #cbd5e1; padding: 0 1px; margin: 0; outline: none; user-select: text; }
.pn-editor .pn-title:hover, .pn-editor .pn-title:focus { border-bottom: 1px solid #e65100; background: #fff; }
.pn-editor .pn-title::placeholder { color: #94a3b8; font-weight: 400; }
.pn-editor .pn-ro .pn-title { border-bottom-color: transparent; background: transparent; pointer-events: none; }
.pn-editor .pn-ro .pn-title.pn-empty { display: none; }
.pn-editor .pn-ro.pn-untitled > .pn-head .pn-paren { display: none; }
.pn-editor p { margin: 2px 0; }
.pn-editor table { border-collapse: collapse; width: 100%; margin: 6px 0; font-size: 13px; }
.pn-editor th, .pn-editor td { border: 1px solid #d9dee5; padding: 4px 6px; vertical-align: top; }
.pn-editor .selectedCell { background: #fff3e0; }
.pn-editor .pn-active { outline: 2px solid #e65100; outline-offset: 2px; }
`;
